import {
  EXCLUSIVE_LOGIN_METHOD,
  LOGIN_SUPERSEDED_BODY,
  LOGIN_SUPERSEDED_STATUS,
  isLoginSupersededBody,
} from "src/domains/shared/api/exclusiveLogin/exclusiveLogin.model";
import {
  checkLoginStatus,
  exclusiveLoginUrl,
} from "src/domains/shared/api/exclusiveLogin/exclusiveLogin.repository";
import { readStoredIdToken } from "src/domains/shared/utils/injectCognitoSession";
import { dismissAllNotices, muteErrorNotices } from "src/domains/shared/utils/notice";
import { isLoginClaimPending } from "./loginClaimPending";

/**
 * 로그인 배타성 fetch 관문 — 전역 `window.fetch` 래퍼 하나 (design D5). 밀려남을 감지하는 데 그치지 않고, 모드에
 * 따라 대상 요청을 네트워크에 보내지 않은 채 거절하거나(로그아웃 정지·점유 대기) 합성 응답으로 답한다(밀려남).
 *
 * main-api 로 가는 요청은 전부 호출 시점에 전역 `fetch` 를 찾는다(Amplify REST·raw fetch·SSE 핸드셰이크).
 * 그래서 이 얇은 래퍼 하나가 화면별 배선 없이 모든 전송 경로를 덮는다. 래퍼는 설치 시점의 원 `fetch` 에 호출마다
 * 위임하므로 다른 계측(Sentry 등)의 체인을 보존한다.
 *
 * 대상은 main-api 오리진(SSM `dronesquare_backend`)과 같은 오리진의 FlightHub 진입 경로뿐이다. 다른 호스트
 * (Cognito·S3·CDN 등)는 그대로 통과한다. 대상 요청은 네 모드로 다룬다.
 * - 정상: 응답을 그대로 돌려주되, `401` + 본문 `code: LoginSuperseded` 면 밀려남으로 감지한다. 그 요청의 자격이
 *   지금 저장된 로그인과 다르면 현재 로그인으로 다시 확인한다(낡은 자격 가드, design D7).
 * - 점유 대기(`isLoginClaimPending()`): 점유 `PUT` 외의 요청을 네트워크 없이 거절한다(design D4).
 * - 로그아웃 정지(`holdLoginSignOutFetchStop()` 으로 건 정지가 하나라도 남아 있는 동안): 해제 `DELETE` 외의 요청을
 *   네트워크 없이 거절한다. 진행 중 이벤트 스트림은 끊는다. 로그아웃하는 흐름(`useAuth.signOut`)은 로컬 로그아웃이
 *   끝날 때까지, 로그아웃을 전해 받은 처리(`useLoginSessionWatcher`)는 이 브라우저의 세션 키가 사라진 것을 볼
 *   때까지(상한 10초) 정지를 건다. 한 탭에서 둘이 겹치면 둘 다 풀려야 끝난다.
 * - 밀려남(처리기 진행 중): 네트워크 없이 합성 밀려남 `401` 을 돌려준다. 진행 중 이벤트 스트림은 끊는다.
 */

/** 토큰을 실어 main-api 를 대신 부르는 같은 오리진 서버 경로. 서버가 밀려남 신호를 그대로 중계한다(design D11). */
const FLIGHTHUB_ENTRY_PATH = "/api/flighthub/entry";
const EVENT_STREAM_MEDIA_TYPE = "text/event-stream";

/**
 * 관문 거절(점유 대기·로그아웃 정지)의 모양 — 브라우저의 네트워크 실패(`TypeError("Failed to fetch")`)와 같게 둔다.
 * 호출부는 기존 네트워크 실패 처리를 그대로 탄다. 관문이 만든 거절인지는 `cause` 의 표식으로만 가린다.
 */
const GATE_REJECTION_MESSAGE = "Failed to fetch";

/**
 * 관문이 점유 대기·로그아웃 정지 중에 네트워크 없이 거절한 요청의 표식 — 거절 `TypeError` 의 `cause` 에 싣는다.
 * 이름·메시지는 진짜 네트워크 실패와 같으므로 둘을 가르는 것은 이 표식뿐이다. 판별은
 * `isExclusiveLoginGateRejection` 으로 한다.
 */
export const EXCLUSIVE_LOGIN_GATE_REJECTION = Symbol("exclusiveLoginGateRejection");

const gateRejection = () =>
  new TypeError(GATE_REJECTION_MESSAGE, { cause: EXCLUSIVE_LOGIN_GATE_REJECTION });

type FetchArgs = Parameters<typeof fetch>;
type FetchInput = FetchArgs[0];
type FetchInit = FetchArgs[1];

type ObservedRequest = {
  url: URL;
  method: string;
  acceptsEventStream: boolean;
  signal: AbortSignal | null | undefined;
  /** `Authorization` 값에서 `Bearer ` 접두를 뗀 토큰 원문. 없으면 null. */
  credential: string | null;
};

/** main-api 오리진과 점유 경로(`…/v1/users/me/login` 의 pathname). */
type BackendLocation = { origin: string; exclusiveLoginPathname: string };

let installed = false;
let supersededMode = false;
/**
 * 지금 걸려 있는 로그아웃 정지들 — 하나라도 있으면 정지 중이다. `holdLoginSignOutFetchStop()` 이 부를 때마다 하나를
 * 더하고, 그것이 돌려준 함수만 그 하나를 뺀다. 로그아웃하는 흐름과 로그아웃을 전해 받은 처리가 한 탭에서 겹쳐도
 * 먼저 끝난 쪽이 다른 쪽의 정지를 풀지 않는다.
 */
const signOutStopHolds = new Set<symbol>();
/** 진행 중인 이벤트 스트림 요청 — 밀려남·로그아웃 정지 모드에 들어갈 때 모두 끊는다. */
const eventStreamControllers = new Set<AbortController>();

const isRequestObject = (input: unknown): input is Request =>
  typeof input === "object" &&
  input !== null &&
  typeof (input as Request).url === "string" &&
  typeof (input as Request).method === "string";

const trimTrailingSlash = (pathname: string) => pathname.replace(/\/+$/, "");

/** 문자열(상대 경로 포함)·`URL`·`Request` 입력을 절대 URL 로 정규화한다. 해석할 수 없으면 null. */
function resolveRequestUrl(input: FetchInput): URL | null {
  const raw = isRequestObject(input) ? input.url : String(input);
  try {
    return new URL(raw, window.location.href);
  } catch {
    return null;
  }
}

/** 헤더 값 하나를 대소문자 구분 없이 읽는다. `Headers`·배열·평범한 객체를 모두 받는다. */
function readHeader(headers: HeadersInit | undefined, name: string): string | null {
  if (!headers) return null;
  if (typeof (headers as Headers).get === "function") return (headers as Headers).get(name);
  const entries = Array.isArray(headers) ? headers : Object.entries(headers);
  const entry = entries.find(([key]) => key.toLowerCase() === name);
  return entry ? String(entry[1]) : null;
}

function describeRequest(input: FetchInput, init: FetchInit): ObservedRequest | null {
  const url = resolveRequestUrl(input);
  if (!url) return null;
  const request = isRequestObject(input) ? input : null;
  // init 이 주는 값이 Request 의 값을 대신한다(fetch 의 합성 규칙과 같다)
  const headers = init?.headers ?? request?.headers;
  const accept = readHeader(headers, "accept");
  const authorization = readHeader(headers, "authorization");
  return {
    url,
    method: (init?.method ?? request?.method ?? "GET").toUpperCase(),
    acceptsEventStream: accept?.toLowerCase().includes(EVENT_STREAM_MEDIA_TYPE) ?? false,
    signal: init?.signal ?? request?.signal,
    // raw fetch·SSE 는 `Bearer <ID 토큰>`, Amplify REST 는 ID 토큰 원문을 싣는다
    credential: authorization?.replace(/^Bearer\s+/i, "").trim() || null,
  };
}

/** SSM 값이 없거나 깨졌으면 null — 그때 관문은 main-api 요청을 알아보지 못해 막지 않고 통과시킨다. */
function resolveBackendLocation(): BackendLocation | null {
  try {
    const claimUrl = new URL(exclusiveLoginUrl());
    return { origin: claimUrl.origin, exclusiveLoginPathname: claimUrl.pathname };
  } catch {
    return null;
  }
}

function isFlightHubEntry(url: URL): boolean {
  return (
    url.origin === window.location.origin && trimTrailingSlash(url.pathname) === FLIGHTHUB_ENTRY_PATH
  );
}

function isBackendBound(url: URL, backend: BackendLocation | null): boolean {
  return url.origin === backend?.origin || isFlightHubEntry(url);
}

function isExclusiveLoginPath(url: URL, backend: BackendLocation | null): boolean {
  return (
    backend !== null &&
    url.origin === backend.origin &&
    trimTrailingSlash(url.pathname) === backend.exclusiveLoginPathname
  );
}

function supersededResponse(): Response {
  return new Response(JSON.stringify(LOGIN_SUPERSEDED_BODY), {
    status: LOGIN_SUPERSEDED_STATUS,
    headers: { "content-type": "application/json" },
  });
}

/** 본문은 `401` 일 때만, 복제본으로 읽는다. 2xx 와 스트림 본문은 건드리지 않는다. */
async function carriesSupersededSignal(response: Response): Promise<boolean> {
  if (response.status !== LOGIN_SUPERSEDED_STATUS) return false;
  try {
    return isLoginSupersededBody(await response.clone().text());
  } catch {
    return false;
  }
}

/**
 * 낡은 자격 가드 (design D7). 밀려남 신호를 받은 요청의 자격이 지금 저장된 ID 토큰과 같을 때만 그 로그인의 밀려남으로
 * 확정한다: 오류 토스트 억제를 켜고 떠 있는 토스트를 닫은 뒤 처리기를 부른다(응답 전달 전, 동기). 자격이 다르면 —
 * 연결 시점 헤더를 고정한 SSE 재연결이나 토큰 교체 전에 출발한 요청 — 억제·토스트 닫기도 처리기도 바로 시작하지 않고,
 * 현재 로그인으로 상태를 다시 확인해 `superseded` 일 때만 처리기를 부른다. 저장된 로그인이 없으면 아무것도 하지
 * 않는다(다른 탭의 신호가 정리를 맡는다). 토큰은 문자열로 비교할 뿐 해석하지 않는다.
 */
function reportSupersededSignal(credential: string | null, onSuperseded: () => void): void {
  const storedIdToken = readStoredIdToken();
  if (storedIdToken === null) return;
  if (credential === storedIdToken) {
    muteErrorNotices();
    dismissAllNotices();
    onSuperseded();
    return;
  }
  void checkLoginStatus().then((status) => {
    if (status === "superseded") onSuperseded();
  });
}

/**
 * 이벤트 스트림 요청은 관문의 컨트롤러를 거쳐 보낸다. 호출부의 signal 이 끊기면 함께 끊기고, 밀려남·로그아웃 정지
 * 모드에 들어가면 관문이 끊는다. `fetch-event-source` 는 그렇게 끊긴 연결을 1초 뒤 재시도한다. 밀려남 중의 재시도는
 * 합성 밀려남 `401` 을 받아 종료 경로로 가고, 로그아웃 정지 중의 재시도는 관문 거절(네트워크 실패 모양)을 받는다.
 * 정지가 풀린 뒤의 재시도는 연결 때 고정한 헤더(이전 ID 토큰)를 그대로 실어 해제된 로그인의 거절을 받을 수 있지만, 그때
 * 저장된 로그인이 없으면 관문은 처리기를 부르지 않는다(낡은 자격 가드). 대개는 그 전에 화면 이동으로 구독이 닫힌다.
 */
async function fetchAbortableEventStream(
  originalFetch: typeof fetch,
  input: FetchInput,
  init: FetchInit,
  callerSignal: AbortSignal | null | undefined
): Promise<Response> {
  const controller = new AbortController();
  const forwardCallerAbort = () => controller.abort(callerSignal?.reason);
  const forget = () => {
    eventStreamControllers.delete(controller);
    callerSignal?.removeEventListener("abort", forwardCallerAbort);
  };

  if (callerSignal?.aborted) forwardCallerAbort();
  else callerSignal?.addEventListener("abort", forwardCallerAbort, { once: true });
  controller.signal.addEventListener("abort", forget, { once: true });
  eventStreamControllers.add(controller);

  try {
    return await originalFetch.call(window, input, { ...init, signal: controller.signal });
  } catch (error) {
    forget();
    throw error;
  }
}

/**
 * `window.fetch` 를 로그인 배타성 관문으로 한 번 감싼다(브라우저 전용, 다시 불러도 무동작). 감지만 하는 래퍼가
 * 아니다 — 대상 요청(main-api 오리진·동일 오리진 FlightHub 진입)은 모드에 따라 네트워크에 나가지 못한다. 대상이
 * 아닌 요청은 모드와 무관하게 인자 그대로 원 `fetch` 에 넘긴다. 대상 요청의 모드는 이 순서로 판정한다.
 *
 * 1. 밀려남(`enterLoginSupersededFetchMode()` ~ `exitLoginSupersededFetchMode()`): 네트워크 없이 합성 밀려남
 *    `401` 을 돌려준다(점유 대기 중이어도). 진행 중인 이벤트 스트림은 모드에 들어갈 때 끊긴다.
 * 2. 로그아웃 정지(`holdLoginSignOutFetchStop()` 으로 건 정지가 하나라도 남아 있는 동안): 해제 `DELETE` 외의
 *    요청을 네트워크 없이 3 과 같은 모양으로 거절한다. 진행 중인 이벤트 스트림은 정지를 걸 때 끊긴다.
 * 3. 점유 대기(`isLoginClaimPending()`): 점유 `PUT` 외의 요청을 네트워크 없이 `TypeError("Failed to fetch")` 로
 *    거절한다. 브라우저의 네트워크 실패와 같은 모양이라 호출부는 기존 실패 처리를 탄다. 거절의 `cause` 에는 관문
 *    표식(`EXCLUSIVE_LOGIN_GATE_REJECTION`)이 실려, `isExclusiveLoginGateRejection` 으로 진짜 네트워크 실패와 가린다.
 * 4. 정상: 요청을 원 `fetch` 로 보내고(이벤트 스트림은 관문의 컨트롤러를 거친다) 응답 객체를 그대로 돌려준다.
 *    지금 로그인의 자격으로 받은 밀려남 신호를 감지하면 오류 토스트 억제를 켜고 떠 있는 토스트를 닫은 뒤
 *    `onSuperseded` 를 부르고(기다리지 않는다), 그 다음에 응답을 호출부에 돌려준다. 억제가 응답 전달보다
 *    먼저라서 호출부의 실패 토스트가 억제보다 먼저 뜨지 않는다 — 단, 억제는 `notice("error", …)` 로 띄우는
 *    토스트에만 닿고 직접 부른 `toast.error` 는 막지 못한다. 다른 자격으로 받은 신호는 현재 로그인으로 다시
 *    확인한다(`reportSupersededSignal`). 로그인 배타성 API(점유 `PUT`·상태 `GET`·해제 `DELETE`)의 응답은 어느
 *    메서드든 감지하지 않는다 — 그것을 부른 흐름(로그인·열린 화면 감시·SSO 재진입 확인·로그아웃·낡은 자격 재확인)이
 *    결과를 스스로 다룬다.
 */
export function installExclusiveLoginFetchGate({
  onSuperseded,
}: {
  onSuperseded: () => void;
}): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const originalFetch = window.fetch;

  window.fetch = async function exclusiveLoginGatedFetch(...args: FetchArgs): Promise<Response> {
    const [input, init] = args;
    const request = describeRequest(input, init);
    const backend = resolveBackendLocation();
    if (!request || !isBackendBound(request.url, backend)) {
      return originalFetch.apply(window, args);
    }

    if (supersededMode) return supersededResponse();

    const onExclusiveLoginPath = isExclusiveLoginPath(request.url, backend);
    const isRelease = onExclusiveLoginPath && request.method === EXCLUSIVE_LOGIN_METHOD.release;
    if (signOutStopHolds.size > 0 && !isRelease) throw gateRejection();
    const isClaim = onExclusiveLoginPath && request.method === EXCLUSIVE_LOGIN_METHOD.claim;
    if (isLoginClaimPending() && !isClaim) throw gateRejection();

    const response = request.acceptsEventStream
      ? await fetchAbortableEventStream(originalFetch, input, init, request.signal)
      : await originalFetch.apply(window, args);

    if (!onExclusiveLoginPath && (await carriesSupersededSignal(response))) {
      reportSupersededSignal(request.credential, onSuperseded);
    }
    return response;
  };
}

/**
 * 밀려남 처리(design D7)가 시작될 때 부른다. 이후 대상 요청은 네트워크 없이 합성 밀려남 `401` 을 받고,
 * 진행 중인 이벤트 스트림 연결은 끊긴다.
 */
export function enterLoginSupersededFetchMode(): void {
  supersededMode = true;
  abortEventStreams();
}

/** 밀려남 처리가 끝나면 부른다. 이후 요청은 다시 네트워크로 나간다(새 로그인의 점유가 막히지 않게). */
export function exitLoginSupersededFetchMode(): void {
  supersededMode = false;
}

/**
 * 이 탭의 백그라운드 작업을 멈추는 로그아웃 정지를 하나 건다. 명시적 로그아웃이 해제 전에(스펙 「명시적 로그아웃은
 * 전파·해제·로컬 로그아웃 순서로 진행한다」), 로그아웃을 전해 받은 탭이 신호를 받을 때(스펙 「밀려남과 로그아웃은
 * 같은 오리진의 다른 탭에 전파된다」) 부른다. 건 정지가 하나라도 남아 있는 동안 대상 요청 중 해제 `DELETE` 만
 * 네트워크로 나가고 나머지는 관문 거절을 받는다. 걸 때 진행 중인 이벤트 스트림 연결을 끊는다. 이미 네트워크로 나간
 * 요청은 되돌리지 못한다.
 *
 * 돌려준 함수가 이 정지 하나를 푼다. 처음 부를 때만 풀고 다시 불러도 무동작이라, 다른 쪽이 건 정지를 풀지 않는다.
 * 건 정지가 모두 풀려야 이후 요청이 다시 네트워크로 나간다(로그아웃 뒤의 공개 요청·새 로그인).
 */
export function holdLoginSignOutFetchStop(): () => void {
  const hold = Symbol("loginSignOutFetchStop");
  signOutStopHolds.add(hold);
  abortEventStreams();
  return () => {
    signOutStopHolds.delete(hold);
  };
}

function abortEventStreams(): void {
  const inFlight = [...eventStreamControllers];
  eventStreamControllers.clear();
  inFlight.forEach((controller) => controller.abort());
}

/**
 * 오류가 관문의 거절(점유 대기·로그아웃 정지)인지 가린다. 거절 `TypeError` 자체(raw `fetch`·SSE 가 받는 것)와, Amplify REST 가 그것을
 * `underlyingError` 로 감싼 `NetworkError` 를 알아본다. 진짜 네트워크 실패와, 그 밖의 방식으로 거절을 다시 감싼
 * 오류(`new Error(…, { cause: 거절 })` 등)는 `false` 다. 밀려남 모드의 합성 `401` 은 거절이 아니라 응답이므로
 * 여기서 가리지 않는다.
 */
export function isExclusiveLoginGateRejection(error: unknown): boolean {
  const candidate = error as
    | { cause?: unknown; underlyingError?: { cause?: unknown } | null }
    | null
    | undefined;
  return (
    candidate?.cause === EXCLUSIVE_LOGIN_GATE_REJECTION ||
    candidate?.underlyingError?.cause === EXCLUSIVE_LOGIN_GATE_REJECTION
  );
}
